import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Collapse,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import SearchIcon from '@material-ui/icons/Search';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';

import { t, number } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

const defaultFilters = {
  idposto: '',
  codigoatendimento: '',
  dataInicio: moment().subtract(7, 'days').format('YYYY-MM-DD'),
};

function possuiFiltroPesquisa(filters) {
  return Boolean(
    filters.idposto || filters.codigoatendimento || filters.dataInicio
  );
}

function descricaoExcecao(value) {
  if (value === 'S') return 'Sim';
  if (value === 'N') return 'Não';
  return 'Ainda não preenchido';
}

function PerdaSLA() {
  const [loading, setLoading] = useState(false);
  const [itensPreenchidos, setItensPreenchidos] = useState('N');
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [openedIds, setOpenedIds] = useState([]);
  const [metas, setMetas] = useState([]);
  const [regras, setRegras] = useState([]);
  const [postos, setPostos] = useState([]);
  const [subitensAdm, setSubitensAdm] = useState({});
  const initialSearchRef = useRef(false);
  const filtrosHabilitados = itensPreenchidos === 'S';

  const handleError = useCallback((err) => {
    if (isArray(err)) err.map((e) => toast.error(t(e)));
    else toast.error(t(err));
  }, []);

  const loadRows = useCallback(async () => {
    try {
      if (filtrosHabilitados && !possuiFiltroPesquisa(filters)) {
        setRows([]);
        setOpenedIds([]);
        toast.error('Informe ao menos um filtro para pesquisar.');
        return;
      }

      setLoading(true);
      const params = {
        itenspreenchidos: itensPreenchidos,
        ...(filtrosHabilitados && filters.idposto
          ? { idposto: filters.idposto }
          : {}),
        ...(filtrosHabilitados && filters.codigoatendimento
          ? { codigoatendimento: filters.codigoatendimento }
          : {}),
        ...(filtrosHabilitados && filters.dataInicio
          ? {
              datainicio: moment(filters.dataInicio).format(
                'DD/MM/YYYY 00:00:00'
              ),
            }
          : {}),
      };

      const { data } = await api.get('pitstop/indicadoroperacaoperdasla', {
        params,
      });
      const nextRows = data || [];

      setRows(nextRows);
      setOpenedIds([]);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [filters, filtrosHabilitados, handleError, itensPreenchidos]);

  const loadCombos = useCallback(async () => {
    try {
      const [metaRes, regraRes, postoRes] = await Promise.all([
        api.get('excecoesmetas', {
          params: {
            pagesize: 99999,
            wherefield: ['statusexcecaometa'],
            valuewherefield: ['L'],
            typewherefield: ['EQUALS'],
            orderfield: 'numeroexcecaometa',
            ordination: 'asc',
            pesquisalista: true,
          },
          paramsSerializer: (q) => qs.stringify(q),
        }),
        api.get('observacaoatendimento/regra', {
          params: {
            pagesize: 99999,
            wherefield: ['statusobservacao'],
            valuewherefield: ['L'],
            typewherefield: ['EQUALS'],
            orderfield: 'descricaoobservacao',
            ordination: 'asc',
            pesquisalista: true,
          },
          paramsSerializer: (q) => qs.stringify(q),
        }),
        api.get('postos', {
          params: { pagesize: 99999, pesquisalista: true },
        }),
      ]);

      setMetas(metaRes.data?.items || metaRes.data || []);
      setRegras(regraRes.data?.items || regraRes.data || []);
      setPostos(postoRes.data?.items || postoRes.data || []);
    } catch (err) {
      handleError(err);
    }
  }, [handleError]);

  const loadSubitensAdm = useCallback(
    async (idobservacaoatendimento) => {
      if (!idobservacaoatendimento || subitensAdm[idobservacaoatendimento]) {
        return;
      }

      try {
        const { data } = await api.get(
          `observacaoatendimento/regra/${idobservacaoatendimento}/itens`
        );
        setSubitensAdm((state) => ({
          ...state,
          [idobservacaoatendimento]: data || [],
        }));
      } catch (err) {
        handleError(err);
      }
    },
    [handleError, subitensAdm]
  );

  useEffect(() => {
    loadCombos();
  }, [loadCombos]);

  useEffect(() => {
    if (initialSearchRef.current) return;
    initialSearchRef.current = true;
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    openedIds.forEach((idatendimento) => {
      const row = rows.find((item) => item.idatendimento === idatendimento);
      if (row?.idobservacaoatendimentoadm) {
        loadSubitensAdm(row.idobservacaoatendimentoadm);
      }
    });
  }, [loadSubitensAdm, openedIds, rows]);

  const updateFilter = useCallback((field, value) => {
    setFilters((state) => ({ ...state, [field]: value }));
  }, []);

  const toggleRow = useCallback((idatendimento) => {
    setOpenedIds((state) =>
      state.includes(idatendimento)
        ? state.filter((id) => id !== idatendimento)
        : [...state, idatendimento]
    );
  }, []);

  const closeRow = useCallback((idatendimento) => {
    setOpenedIds((state) => state.filter((id) => id !== idatendimento));
  }, []);

  const confirmController = useCallback(
    async (row) => {
      if (!row?.idobservacaoatendimentooperacao) {
        toast.error('Nao existe Regra da Operacao para confirmar.');
        return;
      }

      await loadSubitensAdm(row.idobservacaoatendimentooperacao);

      setRows((state) =>
        state.map((item) => {
          if (item.idatendimento !== row.idatendimento) return item;

          return {
            ...item,
            idobservacaoatendimentoadm: row.idobservacaoatendimentooperacao,
            idobservacaoatendimentoitemadm:
              row.idobservacaoatendimentoitemoperacao || '',
            observacaoatendimentoadm:
              item.observacaoatendimentoadm &&
              item.observacaoatendimentoadm.trim()
                ? item.observacaoatendimentoadm
                : 'O controlador concorda com os registros da operação',
          };
        })
      );
    },
    [loadSubitensAdm]
  );

  const updateRow = useCallback((idatendimento, field, value) => {
    setRows((state) =>
      state.map((row) => {
        if (row.idatendimento !== idatendimento) return row;

        const next = { ...row, [field]: value };

        if (field === 'excecaoatendimento' && value !== 'S') {
          next.idexcecaometa = '';
        }

        if (field === 'idobservacaoatendimentoadm') {
          next.idobservacaoatendimentoitemadm = '';
        }

        return next;
      })
    );
  }, []);

  const save = useCallback(
    async (row) => {
      if (!row) return;

      if (row.excecaoatendimento === 'S' && !row.idexcecaometa) {
        toast.error('Informe a Meta para atendimentos marcados como exceção.');
        return;
      }

      if (!row.idobservacaoatendimentoadm) {
        toast.error('Informe a Regra do Controlador.');
        return;
      }

      if (!row.idobservacaoatendimentoitemadm) {
        toast.error('Informe o Subitem da Controlador.');
        return;
      }

      if (
        !row.observacaoatendimentoadm ||
        row.observacaoatendimentoadm.trim().length < 10
      ) {
        toast.error(
          'Informe a Observação do Controlador com no mínimo 10 caracteres.'
        );
        return;
      }

      try {
        setLoading(true);
        const payload = {
          ...row,
          idexcecaometa:
            row.excecaoatendimento === 'S' ? row.idexcecaometa : null,
        };
        const { data } = await api.put(
          'pitstop/indicadoroperacaoperdasla',
          payload
        );
        setRows((state) =>
          state.map((item) =>
            item.idatendimento === data.idatendimento ? data : item
          )
        );
        toast.success(t('s.geral.sucessoalteracao'));
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [handleError]
  );

  return (
    <S.Wrapper>
      {loading && (
        <S.LoadingArea>
          <LinearProgress />
        </S.LoadingArea>
      )}

      <S.Header>
        <Typography variant="h6">Perda de SLA</Typography>
        <S.Filters>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Itens preenchidos"
            value={itensPreenchidos}
            onChange={(event) => setItensPreenchidos(event.target.value)}
          >
            <MenuItem value="N">Não</MenuItem>
            <MenuItem value="S">Sim</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Posto"
            value={filters.idposto}
            onChange={(event) => updateFilter('idposto', event.target.value)}
            disabled={!filtrosHabilitados}
          >
            <MenuItem value="">Todos</MenuItem>
            {postos.map((posto) => (
              <MenuItem key={posto.idposto} value={posto.idposto}>
                {posto.codigonomeposto ||
                  posto.nomeabreviadoposto ||
                  posto.nomeposto}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            variant="outlined"
            label="CO"
            value={filters.codigoatendimento}
            onChange={(event) =>
              updateFilter('codigoatendimento', event.target.value)
            }
            disabled={!filtrosHabilitados}
          />
          <TextField
            size="small"
            variant="outlined"
            label="Data Início"
            type="date"
            value={filters.dataInicio}
            onChange={(event) => updateFilter('dataInicio', event.target.value)}
            disabled={!filtrosHabilitados}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            color="primary"
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={loadRows}
            disabled={loading}
          >
            Pesquisar
          </Button>
        </S.Filters>
      </S.Header>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Posto</TableCell>
              <TableCell>CO</TableCell>
              <TableCell>Loco</TableCell>
              <TableCell>Data Início</TableCell>
              <TableCell>PST</TableCell>
              <TableCell>IPL</TableCell>
              <TableCell>Exceção</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <React.Fragment key={row.idatendimento}>
                <TableRow
                  hover
                  selected={openedIds.includes(row.idatendimento)}
                  onClick={() => toggleRow(row.idatendimento)}
                >
                  <TableCell>{row.codigoposto}</TableCell>
                  <TableCell>{row.codigoatendimento}</TableCell>
                  <TableCell>{row.idveiculo}</TableCell>
                  <TableCell>{row.dataencosteatendimento}</TableCell>
                  <TableCell>{row.slaatendimento}</TableCell>
                  <TableCell>{number(row.ipl || 0, 1)}</TableCell>
                  <TableCell>
                    {row.descricaoexcecaoatendimento ||
                      descricaoExcecao(row.excecaoatendimento)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} style={{ padding: 0 }}>
                    <Collapse
                      in={openedIds.includes(row.idatendimento)}
                      timeout="auto"
                    >
                      <S.Detail>
                        <S.Fields>
                          <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Exceção"
                            value={row?.excecaoatendimento || ''}
                            onChange={(event) =>
                              updateRow(
                                row.idatendimento,
                                'excecaoatendimento',
                                event.target.value
                              )
                            }
                          >
                            <MenuItem value="">Ainda não preenchido</MenuItem>
                            <MenuItem value="S">Sim</MenuItem>
                            <MenuItem value="N">Não</MenuItem>
                          </TextField>
                          <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Meta"
                            value={row?.idexcecaometa || ''}
                            onChange={(event) =>
                              updateRow(
                                row.idatendimento,
                                'idexcecaometa',
                                event.target.value
                              )
                            }
                            disabled={row?.excecaoatendimento !== 'S'}
                          >
                            <MenuItem value="">Selecione</MenuItem>
                            {metas.map((meta) => (
                              <MenuItem
                                key={meta.idexcecaometa}
                                value={meta.idexcecaometa}
                              >
                                {`${meta.numeroexcecaometa} - ${meta.descricaoexcecaometa}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </S.Fields>

                        <S.Sections>
                          <S.Section>
                            <S.SectionTitleRow>
                              <S.SectionTitle>Operação</S.SectionTitle>
                            </S.SectionTitleRow>
                            <S.Fields>
                              <TextField
                                size="small"
                                variant="outlined"
                                label="Regra"
                                value={row?.justificativaoperacao || ''}
                                InputProps={{ readOnly: true }}
                              />
                              <TextField
                                size="small"
                                variant="outlined"
                                label="Subitem"
                                value={row?.subitemoperacao || ''}
                                InputProps={{ readOnly: true }}
                              />
                              <TextField
                                size="small"
                                variant="outlined"
                                label="Responsável"
                                value={row?.codigousuariooperacao || ''}
                                InputProps={{ readOnly: true }}
                              />
                            </S.Fields>
                            <TextField
                              size="small"
                              variant="outlined"
                              label="Observação"
                              value={row?.observacaooperacao || ''}
                              InputProps={{ readOnly: true }}
                              fullWidth
                              multiline
                              rows={3}
                            />
                          </S.Section>

                          <S.Section>
                            <S.SectionTitleRow>
                              <S.SectionTitle>Controlador</S.SectionTitle>
                              {!row?.idobservacaoatendimentoadm && (
                                <S.ConfirmLink
                                  type="button"
                                  onClick={() => confirmController(row)}
                                >
                                  Confirmar
                                </S.ConfirmLink>
                              )}
                            </S.SectionTitleRow>
                            <S.Fields>
                              <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Regra"
                                value={row?.idobservacaoatendimentoadm || ''}
                                onChange={(event) =>
                                  updateRow(
                                    row.idatendimento,
                                    'idobservacaoatendimentoadm',
                                    event.target.value
                                  )
                                }
                              >
                                <MenuItem value="">Selecione</MenuItem>
                                {regras.map((regra) => (
                                  <MenuItem
                                    key={regra.idobservacaoatendimento}
                                    value={regra.idobservacaoatendimento}
                                  >
                                    {regra.descricaoobservacao}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                size="small"
                                variant="outlined"
                                label="Subitem"
                                value={
                                  row?.idobservacaoatendimentoitemadm || ''
                                }
                                onChange={(event) =>
                                  updateRow(
                                    row.idatendimento,
                                    'idobservacaoatendimentoitemadm',
                                    event.target.value
                                  )
                                }
                                disabled={!row?.idobservacaoatendimentoadm}
                              >
                                <MenuItem value="">Selecione</MenuItem>
                                {(
                                  subitensAdm[
                                    row?.idobservacaoatendimentoadm
                                  ] || []
                                ).map((item) => (
                                  <MenuItem
                                    key={item.idobservacaoatendimentoitem}
                                    value={item.idobservacaoatendimentoitem}
                                  >
                                    {item.descricaoobservacaoatendimentoitem}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                size="small"
                                variant="outlined"
                                label="Responsável"
                                value={row?.codigousuarioadm || ''}
                                InputProps={{ readOnly: true }}
                              />
                            </S.Fields>
                            <TextField
                              size="small"
                              variant="outlined"
                              label="Observação"
                              value={row?.observacaoatendimentoadm || ''}
                              onChange={(event) =>
                                updateRow(
                                  row.idatendimento,
                                  'observacaoatendimentoadm',
                                  event.target.value
                                )
                              }
                              inputProps={{ maxLength: 1000 }}
                              fullWidth
                              multiline
                              rows={3}
                            />
                          </S.Section>
                        </S.Sections>

                        <S.Actions>
                          <Button
                            variant="outlined"
                            onClick={() => closeRow(row.idatendimento)}
                          >
                            Fechar
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => save(row)}
                          >
                            Salvar
                          </Button>
                        </S.Actions>
                      </S.Detail>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </S.Wrapper>
  );
}

export default PerdaSLA;
